import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  isAnyOf,
} from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import moment from 'moment';

import LoadingStatus from '@libs/loading-status';

import { PLAN_TRIAL } from '@app/features/subscription/constants';
import { getPlanInterval } from '@app/features/subscription/libs/getPlanInterval';
import AvailableFeatureAction from '@app/features/subscription/types/AvailableFeatureAction';
import AvailableFeatureItem from '@app/features/subscription/types/AvailableFeatureItem';
import { FeatureType } from '@app/features/subscription/types/FeatureType';
import FeatureUsageItem from '@app/features/subscription/types/FeatureUsageItem';
import PaymentItem from '@app/features/subscription/types/PaymentItem';
import { PaymentItemType } from '@app/features/subscription/types/PaymentItemType';
import PlanItem from '@app/features/subscription/types/PlanItem';
import { ProductStatus } from '@app/features/subscription/types/ProductStatus';
import Seat from '@app/features/subscription/types/Seat';
import Subscription from '@app/features/subscription/types/Subscription';
import SubscriptionNextPlan from '@app/features/subscription/types/SubscriptionNextPlan';
import SubscriptionPlan from '@app/features/subscription/types/SubscriptionPlan';
import SubscriptionStatus from '@app/features/subscription/types/SubscriptionStatus';
import workspaceService from '@app/libs/workspaceService';
import {
  SUBSCRIPTION_DATA_FETCH_FAILURE,
  SUBSCRIPTION_DATA_FETCH_REQUEST,
  SUBSCRIPTION_DATA_FETCH_SUCCESS,
  SUBSCRIPTION_DATA_INITIALIZE,
  SUBSCRIPTION_TRIAL_PLAN_START_FAILURE,
  SUBSCRIPTION_TRIAL_PLAN_START_REQUEST,
  SUBSCRIPTION_TRIAL_PLAN_START_SUCCESS,
  SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_FAILURE,
  SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_REQUEST,
  SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_SUCCESS,
  SUBSCRIPTION_TRIAL_PLAN_VALIDATE_FAILURE,
  SUBSCRIPTION_TRIAL_PLAN_VALIDATE_REQUEST,
  SUBSCRIPTION_TRIAL_PLAN_VALIDATE_SUCCESS,
} from '@app/store/actionTypes';
import TransportLayerAvailableFeatureItem from '@app/store/transport-layer/subscription/AvailableFeatureItem';
import TransportLayerFeatureUsageItem from '@app/store/transport-layer/subscription/FeatureUsageItem';
import TransportLayerPlanItem from '@app/store/transport-layer/subscription/PlanItem';
import {
  createSubscription as createSubscriptionAPI,
  GetSubscriptionResponse,
} from '@app/store/transport-layer/subscription/subscription';
import { activateSubscription as activateSubscriptionAPI } from '@app/store/transport-layer/subscription/subscription/activation';
import { createNextPaymentEstimation as createNextPaymentEstimationAPI } from '@app/store/transport-layer/subscription/subscription/next-payment/estimation';
import { createCurrentPaymentEstimation as createCurrentPaymentEstimationAPI } from '@app/store/transport-layer/subscription/subscription/payment/estimation';
import { changeSubscriptionPlan } from '@app/store/transport-layer/subscription/subscription/plan';
import { buyProduct as buyProductAPI } from '@app/store/transport-layer/subscription/subscription/products/buy';
import { resumeSubscriptionAPI } from '@app/store/transport-layer/subscription/subscription/subscribe';
import { pauseSubscriptionAPI } from '@app/store/transport-layer/subscription/subscription/unsubscribe';
import { RootState } from '@app/store/types';

/**
 * 정기결제 조회를 요청한다.
 */
export const fetchSubscriptionData = createAction(SUBSCRIPTION_DATA_FETCH_REQUEST);

/**
 * 정기결제 조회 성공을 알린다.
 */
export const fetchSubscriptionDataSuccess = createAction<Subscription | null>(
  SUBSCRIPTION_DATA_FETCH_SUCCESS
);

/**
 * 정기결제 조회 실패를 알린다.
 */
export const fetchSubscriptionDataFailure = createAction(
  SUBSCRIPTION_DATA_FETCH_FAILURE,
  (payload: AxiosError) => {
    return { payload, error: true };
  }
);

/**
 * 정기결제 정보를 초기화한다.
 */
export const initializeSubscriptionData = createAction(SUBSCRIPTION_DATA_INITIALIZE);

/**
 * 트라이얼 요금제 가능 여부를 확인
 */
export const validateTrialPlan = createAction(SUBSCRIPTION_TRIAL_PLAN_VALIDATE_REQUEST);

/**
 * 트라이얼 요금제 가능 여부 확인 성공을 알림
 */
export const validateTrialPlanSuccess = createAction<{ isAvailableTrialPlan: boolean }>(
  SUBSCRIPTION_TRIAL_PLAN_VALIDATE_SUCCESS
);

/**
 * 트라이얼 요금제 가능 여부 확인 실패를 알림
 */
export const validateTrialPlanFailure = createAction(
  SUBSCRIPTION_TRIAL_PLAN_VALIDATE_FAILURE,
  (payload: AxiosError) => {
    return { payload, error: true };
  }
);

/**
 * 트라이얼 요금제를 시작한다.
 */
export const startTrialPlan = createAction(SUBSCRIPTION_TRIAL_PLAN_START_REQUEST);

/**
 * 트라이얼 요금제 시작 성공을 알린다.
 */
export const startTrialPlanSuccess = createAction(SUBSCRIPTION_TRIAL_PLAN_START_SUCCESS);

/**
 * 트라이얼 요금제 시작 실패를 알린다.
 */
export const startTrialPlanFailure = createAction(
  SUBSCRIPTION_TRIAL_PLAN_START_FAILURE,
  (payload: AxiosError) => {
    return { payload, error: true };
  }
);

/**
 * 트라이얼 요금제를 설문과 함께 시작한다.
 */
export const startTrialWithSurvey = createAction<
  {
    companyCategory?: string;
    companyName?: string;
    companyScale?: string;
    department?: string;
    expectedStartTime?: string;
    expectedUsage?: string;
    userType: string;
    phoneNumber: string;
  },
  typeof SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_REQUEST
>(SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_REQUEST);

/**
 * 트라이얼 요금제 시작과 설문 성공을 알린다.
 */
export const startTrialWithSurveySuccess = createAction<
  {
    companyCategory?: string;
    companyName?: string;
    companyScale?: string;
    department?: string;
    expectedStartTime?: string;
    expectedUsage?: string;
    phoneNumber: string;
    userType: string;
  },
  typeof SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_SUCCESS
>(SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_SUCCESS);

/**
 * 트라이얼 요금제 시작과 설문 실패를 알린다.
 */
export const startTrialWithSurveyFailure = createAction(
  SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_FAILURE,
  (payload: AxiosError) => {
    return { payload, error: true };
  }
);

export type SubscriptionAction =
  | ReturnType<typeof fetchSubscriptionData>
  | ReturnType<typeof fetchSubscriptionDataSuccess>
  | ReturnType<typeof fetchSubscriptionDataFailure>
  | ReturnType<typeof initializeSubscriptionData>
  | ReturnType<typeof startTrialPlan>
  | ReturnType<typeof startTrialPlanSuccess>
  | ReturnType<typeof startTrialPlanFailure>
  | ReturnType<typeof startTrialWithSurvey>
  | ReturnType<typeof startTrialWithSurveySuccess>
  | ReturnType<typeof startTrialWithSurveyFailure>
  | ReturnType<typeof validateTrialPlan>
  | ReturnType<typeof validateTrialPlanSuccess>
  | ReturnType<typeof validateTrialPlanFailure>;

interface ProductItem {
  createdAt: string;
  description: string;
  id: string;
  name: string;
  price: number;
  productAction: FeatureType;
  productType: 'FEATURE_USAGE';
  status: ProductStatus;
  updatedAt: string;
}

const productAdapter = createEntityAdapter<ProductItem>();

function convertDeductionItems(
  data: Array<
    TransportLayerAvailableFeatureItem | TransportLayerFeatureUsageItem | TransportLayerPlanItem
  >
) {
  return data.map((item) => {
    if (item.type === 'PLAN') {
      return {
        ...item,
        interval: item.resetPeriod.value * item.suppliedResetCount,
        type: PaymentItemType[item.type],
      };
    }
    if (item.type === 'FEATURE_USAGE') {
      return {
        ...item,
        action: FeatureType[item.action],
        type: PaymentItemType[item.type],
      };
    }
    return {
      ...item,
      action: AvailableFeatureAction[item.action],
      type: PaymentItemType[item.type],
    };
  });
}

function convertPurchaseItems(
  data: Array<
    TransportLayerAvailableFeatureItem | TransportLayerFeatureUsageItem | TransportLayerPlanItem
  >
) {
  return data.map((item) => {
    if (item.type === 'PLAN') {
      return {
        ...item,
        interval: item.resetPeriod.value * item.suppliedResetCount,
        type: PaymentItemType[item.type],
      };
    }
    if (item.type === 'FEATURE_USAGE') {
      return {
        ...item,
        action: FeatureType[item.action],
        type: PaymentItemType[item.type],
      };
    }
    return {
      ...item,
      action: AvailableFeatureAction[item.action],
      type: PaymentItemType[item.type],
    };
  });
}

export function convertSubscription(data: GetSubscriptionResponse) {
  return data
    ? {
        createdAt: data.createdAt,
        currentTimes: data.currentTimes,
        id: data.id,
        isSubscribing: data.isSubscribing,
        maxTimes: data.maxTimes === -1 ? Infinity : data.maxTimes,
        nextInvoicingDate: data.nextInvoicingDate,
        nextPlan: data.nextPlan && {
          defaultSeat: data.nextPlan.defaultSeat,
          grade: data.nextPlan.grade,
          id: data.nextPlan.id,
          interval: getPlanInterval(data.nextPlan),
          price: data.nextPlan.price,
          resetPeriod: data.nextPlan.resetPeriod,
          seatUnitPrice: data.nextPlan.seatUnitPrice,
          suppliedResetCount: data.nextPlan.suppliedResetCount,
          title: data.nextPlan.title,
        },
        nextResetDate: data.nextResetDate,
        plan: data.plan && {
          applicationId: data.plan.applicationId,
          defaultSeat: data.plan.defaultSeat,
          grade: data.plan.grade,
          id: data.plan.id,
          interval: getPlanInterval(data.plan),
          price: data.plan.price,
          resetPeriod: data.plan.resetPeriod,
          seatUnitPrice: data.plan.seatUnitPrice,
          suppliedResetCount: data.plan.suppliedResetCount,
          title: data.plan.title,
        },
        remainingResetCount: data.remainingResetCount,
        resetDateOfMonth: data.resetDateOfMonth,
        seat: data.seat,
        status: SubscriptionStatus[data.status],
        updatedAt: data.updatedAt,
        userId: data.userId,
      }
    : null;
}

export const activateSubscription = createAsyncThunk(
  'subscription/activateSubscription',
  async () => {
    const result = await activateSubscriptionAPI(workspaceService.getWorkspaceId());
    return convertSubscription(result);
  }
);

export const buyProduct = createAsyncThunk(
  'subscription/buyProduct',
  async (payload: { productId: string; quantity: number }) => {
    const product = await buyProductAPI({
      ...payload,
      workspaceId: workspaceService.getWorkspaceId(),
    });
    return {
      ...product,
      productAction: FeatureType[product.productAction],
      quantity: payload.quantity,
    };
  }
);

export const changeSubscription = createAsyncThunk(
  'subscription/changeSubscription',
  async (param: { items: Array<PaymentItem> }) => {
    const data = await changeSubscriptionPlan(workspaceService.getWorkspaceId(), param);
    return convertSubscription(data);
  }
);

export const createCurrentPaymentEstimation = createAsyncThunk(
  'subscription/createCurrentPaymentEstimation',
  async (payload: Array<PaymentItem>) => {
    const data = await createCurrentPaymentEstimationAPI(
      workspaceService.getWorkspaceId(),
      payload
    );
    return {
      ...data,
      deductionItems: convertDeductionItems(data.deductionItems),
      purchaseItems: convertPurchaseItems(data.purchaseItems),
    };
  }
);

export const createNextPaymentEstimation = createAsyncThunk(
  'subscription/createNextPaymentEstimation',
  async () => {
    const data = await createNextPaymentEstimationAPI(workspaceService.getWorkspaceId());
    return {
      ...data,
      deductionItems: convertDeductionItems(data.deductionItems),
      purchaseItems: convertPurchaseItems(data.purchaseItems),
    };
  }
);

/** 리액트 쿼리 뮤테이션 onSuccess callback으로 redux를 동기화하기 위한 액션입니다. */
export const createSubscriptionSuccess = createAction('CREATE_SUBSCRIPTION_SUCCESS');

export const createSubscription = createAsyncThunk(
  'subscription/createSubscription',
  async (param: { activation: boolean; items: Array<PaymentItem> }) => {
    const data = await createSubscriptionAPI(workspaceService.getWorkspaceId(), param);
    return convertSubscription(data);
  }
);

export const pauseSubscription = createAsyncThunk('subscription/pauseSubscription', async () => {
  const data = await pauseSubscriptionAPI(workspaceService.getWorkspaceId());
  return convertSubscription(data);
});

export const resumeSubscription = createAsyncThunk('subscription/resumeSubscription', async () => {
  const data = await resumeSubscriptionAPI(workspaceService.getWorkspaceId());
  return convertSubscription(data);
});

interface ProductsState extends EntityState<ProductItem> {
  totalCount: number;
  loadingStatus: { buy: LoadingStatus; fetch: LoadingStatus };
}

interface SubscriptionState {
  data: {
    createdAt: string | null;
    currentTimes: number;
    id: string | null;
    isSubscribing: boolean;
    loadingStatus: {
      active: LoadingStatus;
      change: LoadingStatus;
      create: LoadingStatus;
      fetch: LoadingStatus;
      pause: LoadingStatus;
      resume: LoadingStatus;
    };
    maxTimes: number;
    nextInvoicingDate: string | null;
    nextPlan: SubscriptionNextPlan | null;
    nextResetDate: string | null;
    plan: SubscriptionPlan | null;
    remainingResetCount: number;
    resetDateOfMonth: number | null;
    seat: Seat | null;
    status: SubscriptionStatus | null;
    updatedAt: string | null;
    userId: string | null;
  };
  estimation: {
    change: {
      amount: number;
      deductionAmount: number;
      deductionItems: Array<AvailableFeatureItem | FeatureUsageItem | PlanItem> | null;
      finalAmount: number;
      hasError: boolean;
      isImmediateCharge: boolean;
      loadingStatus: LoadingStatus;
      nextPaymentAmount: number;
      prorationMonth: number | null;
      purchaseItems: Array<AvailableFeatureItem | FeatureUsageItem | PlanItem> | null;
    };
    current: {
      amount: number;
      deductionAmount: number;
      deductionItems: Array<AvailableFeatureItem | FeatureUsageItem | PlanItem> | null;
      finalAmount: number;
      hasError: boolean;
      loadingStatus: LoadingStatus;
      prorationMonth: number | null;
      purchaseItems: Array<AvailableFeatureItem | FeatureUsageItem | PlanItem> | null;
    };
    next: {
      amount: number;
      deductionAmount: number;
      deductionItems: Array<AvailableFeatureItem | FeatureUsageItem | PlanItem> | null;
      finalAmount: number;
      hasError: boolean;
      loadingStatus: LoadingStatus;
      prorationMonth: number | null;
      purchaseItems: Array<AvailableFeatureItem | FeatureUsageItem | PlanItem> | null;
    };
  };
  trialPlan: {
    isAvailableTrialPlan: boolean | null;
    isStarting: boolean;
    status: LoadingStatus;
    trialPlanStartWithSurveyStatus: LoadingStatus;
  };
  products: ProductsState;
}

const initialState: SubscriptionState = {
  data: {
    createdAt: null,
    currentTimes: 0,
    id: null,
    isSubscribing: false,
    loadingStatus: {
      active: LoadingStatus.IDLE,
      change: LoadingStatus.IDLE,
      create: LoadingStatus.IDLE,
      fetch: LoadingStatus.IDLE,
      pause: LoadingStatus.IDLE,
      resume: LoadingStatus.IDLE,
    },
    maxTimes: 0,
    nextInvoicingDate: null,
    nextPlan: null,
    nextResetDate: null,
    plan: null,
    remainingResetCount: 0,
    resetDateOfMonth: null,
    seat: null,
    status: null,
    updatedAt: null,
    userId: null,
  },
  estimation: {
    change: {
      amount: 0,
      deductionAmount: 0,
      deductionItems: null,
      finalAmount: 0,
      hasError: false,
      isImmediateCharge: false,
      loadingStatus: LoadingStatus.IDLE,
      nextPaymentAmount: 0,
      prorationMonth: 0,
      purchaseItems: null,
    },
    current: {
      amount: 0,
      deductionAmount: 0,
      deductionItems: null,
      finalAmount: 0,
      hasError: false,
      loadingStatus: LoadingStatus.IDLE,
      prorationMonth: null,
      purchaseItems: null,
    },
    next: {
      amount: 0,
      deductionAmount: 0,
      deductionItems: null,
      finalAmount: 0,
      hasError: false,
      loadingStatus: LoadingStatus.IDLE,
      prorationMonth: null,
      purchaseItems: null,
    },
  },
  trialPlan: {
    isAvailableTrialPlan: null,
    isStarting: false,
    status: LoadingStatus.IDLE,
    trialPlanStartWithSurveyStatus: LoadingStatus.IDLE,
  },
  products: productAdapter.getInitialState({
    totalCount: 0,
    loadingStatus: { buy: LoadingStatus.IDLE, fetch: LoadingStatus.IDLE },
  }),
};

export const { reducer } = createSlice({
  name: 'subscription',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(initializeSubscriptionData, (state) => {
      state.data = { ...initialState.data };
      state.trialPlan = { ...initialState.trialPlan };
    });
    builder.addCase(createCurrentPaymentEstimation.pending, (state) => {
      state.estimation.current.hasError = false;
      state.estimation.current.loadingStatus = LoadingStatus.PENDING;
    });
    builder.addCase(createCurrentPaymentEstimation.fulfilled, (state, action) => {
      if (action.payload) {
        const {
          amount,
          deductionAmount,
          deductionItems,
          finalAmount,
          prorationMonth,
          purchaseItems,
        } = action.payload;
        state.estimation.current.amount = amount;
        state.estimation.current.deductionAmount = deductionAmount;
        state.estimation.current.deductionItems = deductionItems;
        state.estimation.current.finalAmount = finalAmount;
        state.estimation.current.prorationMonth = prorationMonth;
        state.estimation.current.purchaseItems = purchaseItems;
      }
      state.estimation.current.hasError = false;
      state.estimation.current.loadingStatus = LoadingStatus.SUCCEEDED;
    });
    builder.addCase(createCurrentPaymentEstimation.rejected, (state) => {
      state.estimation.current.hasError = true;
      state.estimation.current.loadingStatus = LoadingStatus.FAILED;
    });
    builder.addCase(createNextPaymentEstimation.pending, (state) => {
      state.estimation.next.hasError = false;
      state.estimation.next.loadingStatus = LoadingStatus.PENDING;
    });
    builder.addCase(createNextPaymentEstimation.fulfilled, (state, action) => {
      if (action.payload) {
        const {
          amount,
          deductionAmount,
          deductionItems,
          finalAmount,
          prorationMonth,
          purchaseItems,
        } = action.payload;
        state.estimation.next.amount = amount;
        state.estimation.next.deductionAmount = deductionAmount;
        state.estimation.next.deductionItems = deductionItems;
        state.estimation.next.finalAmount = finalAmount;
        state.estimation.next.prorationMonth = prorationMonth;
        state.estimation.next.purchaseItems = purchaseItems;
      }
      state.estimation.next.hasError = false;
      state.estimation.next.loadingStatus = LoadingStatus.SUCCEEDED;
    });
    builder.addCase(createNextPaymentEstimation.rejected, (state) => {
      state.estimation.next.hasError = true;
      state.estimation.next.loadingStatus = LoadingStatus.FAILED;
    });
    builder.addCase(startTrialPlan, (state) => {
      state.trialPlan.isStarting = true;
      state.trialPlan.status = LoadingStatus.PENDING;
    });
    builder.addCase(startTrialWithSurvey, (state) => {
      state.trialPlan.isStarting = true;
      state.trialPlan.trialPlanStartWithSurveyStatus = LoadingStatus.PENDING;
    });
    builder.addCase(startTrialPlanSuccess, (state) => {
      state.trialPlan.isStarting = false;
      state.trialPlan.isAvailableTrialPlan = false;
      state.trialPlan.status = LoadingStatus.SUCCEEDED;
    });
    builder.addCase(startTrialWithSurveySuccess, (state) => {
      state.trialPlan.isStarting = false;
      state.trialPlan.isAvailableTrialPlan = false;
      state.trialPlan.trialPlanStartWithSurveyStatus = LoadingStatus.SUCCEEDED;
    });
    builder.addCase(startTrialPlanFailure, (state) => {
      state.trialPlan.isStarting = false;
      state.trialPlan.status = LoadingStatus.FAILED;
    });
    builder.addCase(startTrialWithSurveyFailure, (state) => {
      state.trialPlan.isStarting = false;
      state.trialPlan.trialPlanStartWithSurveyStatus = LoadingStatus.FAILED;
    });
    builder.addCase(validateTrialPlan, (state) => {
      state.trialPlan.status = LoadingStatus.PENDING;
    });
    builder.addCase(validateTrialPlanSuccess, (state, action) => {
      state.trialPlan.isAvailableTrialPlan = action.payload.isAvailableTrialPlan;
      state.trialPlan.status = LoadingStatus.SUCCEEDED;
    });
    builder.addCase(validateTrialPlanFailure, (state) => {
      state.trialPlan.status = LoadingStatus.FAILED;
    });
    builder.addCase(buyProduct.pending, (state) => {
      state.products.loadingStatus.buy = LoadingStatus.PENDING;
    });
    builder.addCase(buyProduct.fulfilled, (state) => {
      state.products.loadingStatus.buy = LoadingStatus.SUCCEEDED;
    });
    builder.addCase(buyProduct.rejected, (state) => {
      state.products.loadingStatus.buy = LoadingStatus.FAILED;
    });

    builder.addMatcher(
      isAnyOf(
        activateSubscription.pending,
        changeSubscription.pending,
        createSubscription.pending,
        fetchSubscriptionData,
        pauseSubscription.pending,
        resumeSubscription.pending
      ),
      (state, action) => {
        state.data.loadingStatus = {
          active:
            action.type === activateSubscription.pending.type
              ? LoadingStatus.PENDING
              : state.data.loadingStatus.active,
          change:
            action.type === changeSubscription.pending.type
              ? LoadingStatus.PENDING
              : state.data.loadingStatus.change,
          create:
            action.type === createSubscription.pending.type
              ? LoadingStatus.PENDING
              : state.data.loadingStatus.create,
          fetch:
            action.type === fetchSubscriptionData.type
              ? LoadingStatus.PENDING
              : state.data.loadingStatus.fetch,
          pause:
            action.type === pauseSubscription.pending.type
              ? LoadingStatus.PENDING
              : state.data.loadingStatus.pause,
          resume:
            action.type === resumeSubscription.pending.type
              ? LoadingStatus.PENDING
              : state.data.loadingStatus.resume,
        };
      }
    );
    builder.addMatcher(
      isAnyOf(
        activateSubscription.fulfilled,
        changeSubscription.fulfilled,
        createSubscription.fulfilled,
        fetchSubscriptionDataSuccess,
        pauseSubscription.fulfilled,
        resumeSubscription.fulfilled
      ),
      (state, action) => {
        if (action.payload) {
          state.data = {
            ...action.payload,
            loadingStatus: state.data.loadingStatus,
          };
        }
        state.data.loadingStatus = {
          active:
            action.type === activateSubscription.fulfilled.type
              ? LoadingStatus.SUCCEEDED
              : state.data.loadingStatus.active,
          change:
            action.type === changeSubscription.fulfilled.type
              ? LoadingStatus.SUCCEEDED
              : state.data.loadingStatus.change,
          create:
            action.type === createSubscription.fulfilled.type
              ? LoadingStatus.SUCCEEDED
              : state.data.loadingStatus.create,
          fetch:
            action.type === fetchSubscriptionDataSuccess.type
              ? LoadingStatus.SUCCEEDED
              : state.data.loadingStatus.fetch,
          pause:
            action.type === pauseSubscription.fulfilled.type
              ? LoadingStatus.SUCCEEDED
              : state.data.loadingStatus.pause,
          resume:
            action.type === resumeSubscription.fulfilled.type
              ? LoadingStatus.SUCCEEDED
              : state.data.loadingStatus.resume,
        };
      }
    );
    builder.addMatcher(
      isAnyOf(
        activateSubscription.rejected,
        changeSubscription.rejected,
        createSubscription.rejected,
        fetchSubscriptionDataFailure,
        pauseSubscription.rejected,
        resumeSubscription.rejected
      ),
      (state, action) => {
        state.data.loadingStatus = {
          active:
            action.type === activateSubscription.rejected.type
              ? LoadingStatus.FAILED
              : state.data.loadingStatus.active,
          change:
            action.type === changeSubscription.rejected.type
              ? LoadingStatus.FAILED
              : state.data.loadingStatus.change,
          create:
            action.type === createSubscription.rejected.type
              ? LoadingStatus.FAILED
              : state.data.loadingStatus.create,
          fetch:
            action.type === fetchSubscriptionDataFailure.type
              ? LoadingStatus.FAILED
              : state.data.loadingStatus.fetch,
          pause:
            action.type === pauseSubscription.rejected.type
              ? LoadingStatus.FAILED
              : state.data.loadingStatus.pause,
          resume:
            action.type === resumeSubscription.rejected.type
              ? LoadingStatus.FAILED
              : state.data.loadingStatus.resume,
        };
      }
    );
  },
});

export const getSubscription = (state: RootState) => state.subscription;

export const getSubscriptionData = (state: RootState) => getSubscription(state).data;
const getSubscriptionCreatedAt = (state: RootState) => getSubscriptionData(state).createdAt;
export const getSubscriptionCurrentTimes = (state: RootState) =>
  getSubscriptionData(state).currentTimes;
export const getSubscriptionIsSubscribing = (state: RootState) =>
  getSubscriptionData(state).isSubscribing;
export const getSubscriptionLoadingStatus = (state: RootState) =>
  getSubscriptionData(state).loadingStatus;
export const getSubscriptionMaxTimes = (state: RootState) => getSubscriptionData(state).maxTimes;
export const getSubscriptionNextInvoicingDate = (state: RootState) =>
  getSubscriptionData(state).nextInvoicingDate;
export const getSubscriptionNextPlan = (state: RootState) => getSubscriptionData(state).nextPlan;
export const getSubscriptionNextResetDate = (state: RootState) =>
  getSubscriptionData(state).nextResetDate;
export const getSubscriptionPlan = (state: RootState) => getSubscriptionData(state).plan;
export const getSubscriptionPlanApplicationId = (state: RootState) =>
  getSubscriptionPlan(state)?.applicationId ?? null;
export const getSubscriptionPlanGrade = (state: RootState) =>
  getSubscriptionPlan(state)?.grade ?? null;
export const getSubscriptionPlanId = (state: RootState) => getSubscriptionPlan(state)?.id ?? null;
export const getSubscriptionPlanPrice = (state: RootState) =>
  getSubscriptionPlan(state)?.price ?? null;
const getSubscriptionPlanResetPeriod = (state: RootState) =>
  getSubscriptionPlan(state)?.resetPeriod ?? null;
export const getSubscriptionPlanResetPeriodValue = (state: RootState) =>
  getSubscriptionPlanResetPeriod(state)?.value ?? null;
export const getSubscriptionPlanSeatUnitPrice = (state: RootState) =>
  getSubscriptionPlan(state)?.seatUnitPrice ?? null;
export const getSubscriptionPlanSuppliedResetCount = (state: RootState) =>
  getSubscriptionPlan(state)?.suppliedResetCount ?? null;
export const getSubscriptionPlanTitle = (state: RootState) =>
  getSubscriptionPlan(state)?.title ?? null;
const getSubscriptionSeat = (state: RootState) => getSubscriptionData(state).seat;
/**
 * 요금제에 기본으로 포함된 것 이외에 추가로 구매한 시트
 */
export const getSubscriptionAdditionalSeatQuota = (state: RootState) =>
  getSubscriptionSeat(state)?.quota ?? null;
export const getSubscriptionStatus = (state: RootState) => getSubscriptionData(state).status;
export const getHasActiveSubscription = (state: RootState) =>
  getSubscriptionStatus(state) === SubscriptionStatus.ACTIVE;

export const getIsTrialPlanAppliedToday = (state: RootState): boolean => {
  const planId = getSubscriptionPlanId(state);
  const subscriptionCreatedAt = getSubscriptionCreatedAt(state);

  return planId === PLAN_TRIAL && moment().isSame(subscriptionCreatedAt, 'day');
};

export const getSubscriptionExpiryDate = createSelector(
  getSubscriptionNextInvoicingDate,
  (nextInvoicingDate) => {
    return nextInvoicingDate ? moment(nextInvoicingDate).subtract(1, 'second').toISOString() : null;
  }
);

export const getSubscriptionNextBillingDate = createSelector(
  getSubscriptionNextInvoicingDate,
  (nextInvoicingDate) => {
    return nextInvoicingDate ? moment(nextInvoicingDate).add(7, 'hour').toISOString() : null;
  }
);

export const getSubscriptionPlanInterval = (state: RootState) => {
  const resetPeriodValue = getSubscriptionPlanResetPeriodValue(state);
  const suppliedRestCount = getSubscriptionPlanSuppliedResetCount(state);
  return resetPeriodValue && suppliedRestCount ? resetPeriodValue * suppliedRestCount : 0;
};

export const getIsUsingResellingPlan = (state: RootState) =>
  !!getSubscriptionPlan(state)?.applicationId;

const getEstimation = (state: RootState) => getSubscription(state).estimation;
export const getChangeEstimation = (state: RootState) => getEstimation(state).change;
export const getChangeEstimationLoadingStatus = (state: RootState) =>
  getChangeEstimation(state).loadingStatus;
export const getCurrentEstimation = (state: RootState) => getEstimation(state).current;
export const getCurrentEstimationFinalAmount = (state: RootState) =>
  getCurrentEstimation(state).finalAmount;
export const getCurrentEstimationLoadingStatus = (state: RootState) =>
  getCurrentEstimation(state).loadingStatus;
export const getNextEstimation = (state: RootState) => getEstimation(state).next;
export const getNextEstimationFinalAmount = (state: RootState) =>
  getNextEstimation(state).finalAmount;
export const getNextEstimationLoadingStatus = (state: RootState) =>
  getNextEstimation(state).loadingStatus;

export const getTrialPlan = (state: RootState) => getSubscription(state).trialPlan;
export const getIsAvailableTrialPlan = (state: RootState) =>
  getTrialPlan(state).isAvailableTrialPlan;
export const getStatus = (state: RootState) => getTrialPlan(state).status;
export const getIsStarting = (state: RootState) => getTrialPlan(state).isStarting;
const getProductsState = (state: RootState) => getSubscription(state).products;
export const getProductsLoadingStatus = (state: RootState) => getProductsState(state).loadingStatus;
